import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChange, SimpleChanges } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { FormioCustomComponent } from '@formio/angular';
import { FirebaseStorage } from 'src/app/core/classes/firebase-storage';
import { ItemFile } from 'src/app/core/interfaces/items/item-file';
import { ToastService } from 'src/app/core/shared/toast.service';

@Component({
	selector: 'app-custom-audio-recorder',
	styleUrls: ['./custom-audio-recorder.component.scss'],
	templateUrl: './custom-audio-recorder.component.html'
})
export class CustomAudioRecorderComponent implements FormioCustomComponent<string>, OnChanges, OnDestroy {
	@Input() public value: string = '';
	@Input() public disabled: boolean = false;
	@Input() public maxTime!: number;
	@Output() public valueChange: EventEmitter<string> = new EventEmitter<string>();

	private static pendingBlobUrl: string = '';

	private readonly TIME_INTERVAL: number = 1000;
	private readonly MINUTES_IN_HOUR: number = 60;
	private readonly PAD_START: number = 2;
	private readonly AUDIO_FILE_NAME: string = 'audioRecorder.wav';
	private readonly AUDIO_MIME_TYPE: string = 'audio/wav';

	private readonly firebaseStorage: FirebaseStorage = new FirebaseStorage();

	private mediaRecorder: MediaRecorder | null = null;
	private audioChunks: Blob[] = [];
	private seconds: number = 0;
	private timerInterval: ReturnType<typeof setInterval> | null = null;

	public recordingTime: string = '00:00';
	public isRecording: boolean = false;
	public isUploading: boolean = false;
	public audioUrl: SafeUrl | null = null;

	constructor(
		private readonly cd: ChangeDetectorRef,
		private readonly sanitizer: DomSanitizer,
		private readonly toastService: ToastService
	) {}

	public ngOnChanges(changes: SimpleChanges): void {
		const typedChanges: { value?: SimpleChange } = changes;
		const valueChange: SimpleChange | undefined = typedChanges.value;
		if (valueChange && valueChange.currentValue && !this.audioUrl && CustomAudioRecorderComponent.pendingBlobUrl)
			this.audioUrl = this.sanitizer.bypassSecurityTrustUrl(CustomAudioRecorderComponent.pendingBlobUrl);
	}

	public ngOnDestroy(): void {
		this.stopTimer();
	}

	/**
	 * Starts recording audio from the user's microphone
	 * and sets up the upload-to-Storage handler on stop.
	 */
	public async startRecording(): Promise<void> {
		this.audioChunks = [];
		this.seconds = 0;
		try {
			const stream: MediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });
			this.mediaRecorder = new MediaRecorder(stream);

			this.mediaRecorder.ondataavailable = (event: BlobEvent): void => {
				this.audioChunks.push(event.data);
			};

			this.mediaRecorder.onstop = (): void => {
				this.uploadAudioToStorage();
			};

			this.mediaRecorder.start();
			this.isRecording = true;
			this.startTimer();
		} catch (err) {
			this.toastService.warning('access denied to microphone');
		}
	}

	/**
	 * Uploads the recorded audio blob to Firebase Storage
	 * and emits the resulting filepath as the component value.
	 */
	private async uploadAudioToStorage(): Promise<void> {
		const audioBlob: Blob = new Blob(this.audioChunks, { type: this.AUDIO_MIME_TYPE });
		const audioFile: File = new File([audioBlob], this.AUDIO_FILE_NAME, { type: this.AUDIO_MIME_TYPE });

		CustomAudioRecorderComponent.pendingBlobUrl = URL.createObjectURL(audioBlob);
		this.audioUrl = this.sanitizer.bypassSecurityTrustUrl(CustomAudioRecorderComponent.pendingBlobUrl);
		this.isUploading = true;
		this.cd.detectChanges();

		try {
			const noopProgress: (arg0: { loaded: number; total: number }) => void = (): void => {};
			const itemFile: ItemFile = await this.firebaseStorage.uploadFile(audioFile, audioFile.name, '', noopProgress, '', {});

			this.value = itemFile.Id;
			this.valueChange.emit(this.value);
		} catch (err) {
			this.audioUrl = null;
			CustomAudioRecorderComponent.pendingBlobUrl = '';
			this.toastService.warning('Could not upload audio file');
		} finally {
			this.isUploading = false;
			this.cd.detectChanges();
		}
	}

	/** 
	 * Start the recording timer, updating the recording time every second and stopping if the max time is reached.
	 */
	private startTimer(): void {
		this.stopTimer();
		this.seconds = 0;
		this.recordingTime = '00:00';

		this.timerInterval = setInterval((): void => {
			this.seconds++;

			if (this.maxTime && this.seconds >= this.maxTime) {
				this.stopRecording();
				return;
			}

			const mins: string = Math.floor(this.seconds / this.MINUTES_IN_HOUR).toString().padStart(this.PAD_START, '0');
			const secs: string = (this.seconds % this.MINUTES_IN_HOUR).toString().padStart(this.PAD_START, '0');
			this.recordingTime = `${mins}:${secs}`;
			this.cd.detectChanges();
		}, this.TIME_INTERVAL);
	}

	/** 
	 * Stop the timer interval if it's running.
	 */
	private stopTimer(): void {
		if (this.timerInterval) {
			clearInterval(this.timerInterval);
			this.timerInterval = null;
		}
	}

	/** 
	 * Stop the current recording, finalizing the audio and resetting the recording state.
	 */
	public stopRecording(): void {
		if (this.mediaRecorder) {
			this.mediaRecorder.stop();
			this.isRecording = false;
			this.mediaRecorder.stream.getTracks().forEach((track: MediaStreamTrack): void => track.stop());
			this.stopTimer();
		}
	}

	/** 
	 * Reset the current recording, clearing the value and audio URL.
	 */
	public removeAudio(): void {
		this.value = '';
		this.audioUrl = null;
		CustomAudioRecorderComponent.pendingBlobUrl = '';
		this.audioChunks = [];
		this.valueChange.emit(this.value);
		this.cd.detectChanges();
		this.stopTimer();
	}

	/** 
	 * Get the maximum recording time in minutes based on the maxTime input (which is in seconds).
	 */
	public get maxTimeMinutes(): number {
		if (!this.maxTime)
			return 0;

		return Math.floor(this.maxTime / this.MINUTES_IN_HOUR);
	}

}
